import axios, { type AxiosError } from 'axios'

import i18n from '@/lib/i18n/config'

/** Shape our backend returns for failures (RFC 7807-ish). */
export interface ApiProblem {
  title?: string
  detail?: string
  status?: number
  traceId?: string
  errors?: Record<string, string[]>
}

/**
 * Normalized transport error. Every rejected request from `apiClient` is an
 * `ApiError`, so UI code never has to poke at raw Axios internals.
 */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly traceId: string | undefined
  readonly fieldErrors: Record<string, string[]> | undefined
  readonly isNetworkError: boolean

  constructor(params: {
    message: string
    status: number
    code: string
    traceId?: string | undefined
    fieldErrors?: Record<string, string[]> | undefined
    isNetworkError?: boolean
  }) {
    super(params.message)
    this.name = 'ApiError'
    this.status = params.status
    this.code = params.code
    this.traceId = params.traceId
    this.fieldErrors = params.fieldErrors
    this.isNetworkError = params.isNetworkError ?? false
  }
}

const KNOWN_STATUSES = [400, 401, 403, 404, 409, 422, 500] as const
type KnownStatus = (typeof KNOWN_STATUSES)[number]

function isKnownStatus(status: number): status is KnownStatus {
  return (KNOWN_STATUSES as readonly number[]).includes(status)
}

/** Localized, user-facing message for a status code. */
export function messageForStatus(status: number): string {
  if (isKnownStatus(status)) return i18n.t(`http.${status}`, { ns: 'errors' })
  if (status >= 500) return i18n.t('http.500', { ns: 'errors' })
  return i18n.t('http.unknown', { ns: 'errors' })
}

export function toApiError(error: unknown): ApiError {
  // console.error(error);

  if (error instanceof ApiError) return error

  if (axios.isAxiosError(error)) {
    // `data` really can be absent (HTML error page, empty body) — model it.
    const axiosError = error as AxiosError<ApiProblem | undefined>

    if (axios.isCancel(error)) {
      return new ApiError({
        message: 'Request canceled',
        status: 0,
        code: 'CANCELED',
        isNetworkError: false,
      })
    }

    if (axiosError.code === 'ECONNABORTED') {
      return new ApiError({
        message: i18n.t('http.timeout', { ns: 'errors' }),
        status: 0,
        code: 'TIMEOUT',
        isNetworkError: true,
      })
    }

    if (!axiosError.response) {
      return new ApiError({
        message: i18n.t('http.network', { ns: 'errors' }),
        status: 0,
        code: 'NETWORK',
        isNetworkError: true,
      })
    }

    const { status, data } = axiosError.response
    return new ApiError({
      message: data?.detail ?? data?.title ?? messageForStatus(status),
      status,
      code: `HTTP_${status}`,
      traceId: data?.traceId,
      fieldErrors: data?.errors,
    })
  }

  return new ApiError({
    message: error instanceof Error ? error.message : i18n.t('http.unknown', { ns: 'errors' }),
    status: 0,
    code: 'UNKNOWN',
  })
}
